import React, { useState } from 'react';
import {
    Form, Input, Button, Upload, message, Row, Col, Breadcrumb, Grid, Switch, Tag, Space, Tooltip, Card, Avatar
} from 'antd';
import {
    UploadOutlined, RightOutlined, PlusOutlined, InfoCircleOutlined, HomeOutlined,
    EnvironmentOutlined, SaveOutlined, ArrowLeftOutlined, CheckCircleOutlined,
    TagsOutlined, FileAddOutlined
} from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { acheteTerrainService } from '../../common/services';

const AjoutSitePage = () => {
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.sm;
    const controlSize = isMobile ? 'small' : 'middle';
    const [lotsFile, setLotsFile] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const normFile = (e) => (Array.isArray(e) ? e : e?.fileList);

    const handleSubmit = async (values) => {
        try {
            setIsSubmitting(true);
            let lots = [];
            if (lotsFile) {
                const text = await lotsFile.text();
                const parsed = JSON.parse(text);
                if (!Array.isArray(parsed)) {
                    message.error('Le fichier JSON doit contenir un tableau de lots.');
                    return;
                }
                lots = parsed.map((l) => ({
                    intitule: String(l.intitule || '').trim(),
                    superficieM2: Number(l.superficieM2 || 0),
                    prixTotal: Number(l.prixTotal || 0),
                    remarque: l.remarque || undefined,
                    status: 'disponible',
                })).filter(x => x.intitule && x.superficieM2 > 0 && x.prixTotal > 0);
            }

            const payload = {
                nom: values.nom?.trim(),
                localisation: values.localisation?.trim(),
                estViabilise: !!values.estViabilise,
                caracteristiques: (values.caracteristiques || []).map((s) => String(s).trim()).filter(Boolean),
                lots,
            };

            await acheteTerrainService.createSite(payload);
            message.success('Site créé avec succès');
            navigate('/dashboard/sites');
        } catch (error) {
            console.error(error);
            message.error("Erreur lors de la création du site");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="content-wrapper">
            <style>
                {`
                    .form-card-site {
                        border-radius: 12px;
                        box-shadow: 0 2px 8px rgba(0,0,0,0.06);
                    }
                    .form-card-site .ant-form-item-label > label {
                        font-weight: 500;
                        color: #262626;
                    }
                    .form-card-site .ant-input,
                    .form-card-site .ant-select-selector {
                        border-radius: 8px !important;
                    }
                    .form-card-site .ant-switch-checked {
                        background: linear-gradient(135deg, #52c41a 0%, #73d13d 100%);
                    }
                `}
            </style>

            <div className="container-xxl flex-grow-1" style={{ paddingTop: 8, paddingLeft: 24, paddingRight: 24 }}>
                {/* Breadcrumb */}
                <Breadcrumb
                    separator={<RightOutlined style={{ fontSize: 12, color: '#8c8c8c' }} />}
                    style={{ marginBottom: 12, marginTop: 0, paddingTop: 0 }}
                >
                    <Breadcrumb.Item>
                        <Link to="/dashboard" style={{ color: '#fa8c16' }}>Accueil</Link>
                    </Breadcrumb.Item>
                    <Breadcrumb.Item>
                        <Link to="/dashboard/sites" style={{ color: '#fa8c16' }}>Sites</Link>
                    </Breadcrumb.Item>
                    <Breadcrumb.Item>
                        <span style={{ color: '#595959' }}>Nouveau site</span>
                    </Breadcrumb.Item>
                </Breadcrumb>

                {/* Formulaire */}
                <Form
                    layout="vertical"
                    form={form}
                    onFinish={handleSubmit}
                    initialValues={{ estViabilise: false, caracteristiques: [] }}
                    size={controlSize}
                >
                    <Row gutter={[24, 16]}>
                        {/* Colonne gauche - Informations */}
                        <Col xs={24} lg={12}>
                            <Card
                                className="form-card-site"
                                title={
                                    <span>
                                        <HomeOutlined style={{ color: '#fa8c16', marginRight: 8 }} />
                                        Informations du site
                                    </span>
                                }
                                style={{ height: '100%' }}
                                headStyle={{ borderBottom: '2px solid #f0f0f0' }}
                            >
                                <Form.Item
                                    name="nom"
                                    label="Nom du site"
                                    rules={[{ required: true, message: 'Veuillez saisir le nom du site' }]}
                                >
                                    <Input
                                        placeholder="Ex : Site de Bingerville"
                                        prefix={<HomeOutlined style={{ color: '#bfbfbf' }} />}
                                        style={{ borderRadius: 8 }}
                                    />
                                </Form.Item>

                                <Form.Item
                                    name="localisation"
                                    label="Localisation"
                                    rules={[{ required: true, message: 'Veuillez saisir la localisation' }]}
                                >
                                    <Input
                                        placeholder="Ex : Abidjan – Bingerville"
                                        prefix={<EnvironmentOutlined style={{ color: '#bfbfbf' }} />}
                                        style={{ borderRadius: 8 }}
                                    />
                                </Form.Item>

                                <Form.Item
                                    name="estViabilise"
                                    label={
                                        <Space>
                                            <span>Viabilisé</span>
                                            <Tooltip title="Le terrain dispose de voirie, eau, électricité, etc.">
                                                <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
                                            </Tooltip>
                                        </Space>
                                    }
                                    valuePropName="checked"
                                >
                                    <Switch
                                        checkedChildren={<CheckCircleOutlined />}
                                        unCheckedChildren="Non"
                                    />
                                </Form.Item>

                                <div style={{
                                    padding: 16,
                                    background: '#fff7e6',
                                    borderRadius: 8,
                                    border: '1px solid #ffd591',
                                    marginTop: 16
                                }}>
                                    <h4 style={{ margin: '0 0 8px 0', color: '#d46b08', display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <InfoCircleOutlined /> Conseils
                                    </h4>
                                    <ul style={{ margin: 0, paddingLeft: 20, color: '#8c8c8c', fontSize: 13 }}>
                                        <li>Utilisez un nom clair et descriptif</li>
                                        <li>Précisez la localisation exacte</li>
                                        <li>Indiquez si le terrain est viabilisé</li>
                                    </ul>
                                </div>
                            </Card>
                        </Col>

                        {/* Colonne droite - Options */}
                        <Col xs={24} lg={12}>
                            <Card
                                className="form-card-site"
                                title={
                                    <span>
                                        <TagsOutlined style={{ color: '#fa8c16', marginRight: 8 }} />
                                        Options et caractéristiques
                                    </span>
                                }
                                style={{ marginBottom: 16 }}
                                headStyle={{ borderBottom: '2px solid #f0f0f0' }}
                            >
                                <Form.Item
                                    name="caracteristiques"
                                    label={
                                        <Space>
                                            <span>Caractéristiques (tags)</span>
                                            <Tooltip title="Ex : Clôturé, Électricité, Eau, Voirie, Gardiennage">
                                                <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
                                            </Tooltip>
                                        </Space>
                                    }
                                >
                                    <TagsInput />
                                </Form.Item>
                            </Card>

                            <Card
                                className="form-card-site"
                                title={
                                    <span>
                                        <FileAddOutlined style={{ color: '#fa8c16', marginRight: 8 }} />
                                        Import de lots (optionnel)
                                    </span>
                                }
                                headStyle={{ borderBottom: '2px solid #f0f0f0' }}
                            >
                                <Form.Item
                                    name="lotsFile"
                                    label={
                                        <Space>
                                            <span>Fichier JSON des lots</span>
                                            <Tooltip title='Format attendu: [{"intitule":"Lot A1","superficieM2":300,"prixTotal":3000000,"remarque":"..."}]'>
                                                <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
                                            </Tooltip>
                                        </Space>
                                    }
                                    valuePropName="fileList"
                                    getValueFromEvent={normFile}
                                >
                                    <Upload.Dragger
                                        beforeUpload={() => false}
                                        accept=".json,application/json"
                                        maxCount={1}
                                        onChange={(info) => {
                                            const f = info?.fileList?.[0]?.originFileObj;
                                            setLotsFile(f || null);
                                        }}
                                        showUploadList={false}
                                        style={{
                                            padding: 16,
                                            borderRadius: 12,
                                            border: '2px dashed #d9d9d9',
                                            background: '#fafafa'
                                        }}
                                    >
                                        {lotsFile ? (
                                            <div style={{ textAlign: 'center' }}>
                                                <CheckCircleOutlined style={{ fontSize: 32, color: '#52c41a' }} />
                                                <p style={{ marginTop: 8, marginBottom: 4, color: '#52c41a', fontWeight: 500 }}>
                                                    Fichier sélectionné
                                                </p>
                                                <Tag color="green">{lotsFile.name}</Tag>
                                                <p style={{ margin: '8px 0 0 0', color: '#8c8c8c', fontSize: 12 }}>
                                                    Cliquez pour remplacer
                                                </p>
                                            </div>
                                        ) : (
                                            <div style={{ textAlign: 'center' }}>
                                                <UploadOutlined style={{ fontSize: 32, color: '#fa8c16' }} />
                                                <p style={{ marginTop: 12, marginBottom: 4, color: '#595959' }}>
                                                    Cliquez ou glissez un fichier JSON ici
                                                </p>
                                                <p style={{ margin: 0, color: '#bfbfbf', fontSize: 12 }}>
                                                    Format: tableau de lots avec intitule, superficieM2, prixTotal
                                                </p>
                                            </div>
                                        )}
                                    </Upload.Dragger>
                                </Form.Item>

                                {lotsFile && (
                                    <Button
                                        type="link"
                                        danger
                                        size="small"
                                        onClick={() => {
                                            setLotsFile(null);
                                            form.setFieldValue('lotsFile', []);
                                        }}
                                        style={{ marginTop: 8 }}
                                    >
                                        Supprimer le fichier
                                    </Button>
                                )}
                            </Card>
                        </Col>
                    </Row>

                    {/* Boutons d'action */}
                    <Card className="form-card-site" style={{ marginTop: 24 }} bodyStyle={{ padding: 16 }}>
                        <Row>
                            <Col xs={24}>
                                <div style={{
                                    display: 'flex',
                                    justifyContent: isMobile ? 'stretch' : 'flex-end',
                                    gap: 12,
                                    flexDirection: isMobile ? 'column' : 'row'
                                }}>
                                    <Link to="/dashboard/sites">
                                        <Button
                                            size={controlSize}
                                            style={{
                                                borderRadius: 8,
                                                width: isMobile ? '100%' : 'auto'
                                            }}
                                        >
                                            Annuler
                                        </Button>
                                    </Link>
                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        loading={isSubmitting}
                                        icon={<SaveOutlined />}
                                        size={controlSize}
                                        style={{
                                            borderRadius: 8,
                                            background: 'linear-gradient(135deg, #fa8c16 0%, #ffa940 100%)',
                                            border: 'none',
                                            width: isMobile ? '100%' : 'auto',
                                            minWidth: 180
                                        }}
                                    >
                                        {isSubmitting ? 'Enregistrement...' : 'Enregistrer le site'}
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </Card>
                </Form>
            </div>
        </div>
    );
};

export default AjoutSitePage;

/** ---- Composant pour gérer des tags libres dans un Form antd ---- */
const TagsInput = ({ value = [], onChange }) => {
    const [input, setInput] = useState('');
    const values = Array.isArray(value) ? value : [];

    const tagColors = ['#fa8c16', '#1890ff', '#52c41a', '#722ed1', '#eb2f96', '#13c2c2', '#faad14'];

    const getTagColor = (index) => tagColors[index % tagColors.length];

    const add = () => {
        const v = input.trim();
        if (!v) return;
        const next = Array.from(new Set([...(values || []), v]));
        onChange?.(next);
        setInput('');
    };

    const remove = (tag) => {
        const next = (values || []).filter((t) => t !== tag);
        onChange?.(next);
    };

    return (
        <div>
            {values.length > 0 && (
                <div style={{
                    marginBottom: 12,
                    padding: 12,
                    background: '#fafafa',
                    borderRadius: 8,
                    border: '1px solid #f0f0f0'
                }}>
                    <Space wrap>
                        {values.map((t, index) => (
                            <Tag
                                key={t}
                                closable
                                onClose={() => remove(t)}
                                style={{
                                    borderRadius: 12,
                                    padding: '4px 12px',
                                    background: getTagColor(index),
                                    color: 'white',
                                    border: 'none',
                                    fontWeight: 500
                                }}
                            >
                                {t}
                            </Tag>
                        ))}
                    </Space>
                </div>
            )}
            <Space.Compact style={{ width: '100%' }}>
                <Input
                    placeholder="Ajouter une caractéristique..."
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onPressEnter={(e) => {
                        e.preventDefault();
                        add();
                    }}
                    style={{ borderRadius: '8px 0 0 8px' }}
                />
                <Button
                    type="primary"
                    onClick={add}
                    icon={<PlusOutlined />}
                    style={{
                        borderRadius: '0 8px 8px 0',
                        background: 'linear-gradient(135deg, #fa8c16 0%, #ffa940 100%)',
                        border: 'none'
                    }}
                >
                    Ajouter
                </Button>
            </Space.Compact>
            <p style={{ marginTop: 8, marginBottom: 0, fontSize: 12, color: '#8c8c8c' }}>
                Appuyez sur Entrée ou cliquez sur "Ajouter" pour ajouter un tag
            </p>
        </div>
    );
};
